import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, Alert, Card, CardContent,
  Stack, MenuItem, TextField, useMediaQuery, useTheme,
} from '@mui/material';
import { Download, Receipt, InfoOutlined } from '@mui/icons-material';
import { getMyPayStubs, getMyPaySummary, downloadPayStub } from '../api';
import W2Documents from './W2Documents';

const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

export default function MyPay() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [stubs, setStubs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [stubsRes, summaryRes] = await Promise.all([
        getMyPayStubs(),
        getMyPaySummary(year),
      ]);
      setStubs(stubsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      if (err.response?.status === 400) {
        setError('No employee record linked to your account');
      } else {
        setError('Failed to load pay information');
      }
    }
  }, [year]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDownload = async (stubId) => {
    try {
      await downloadPayStub(stubId);
    } catch (err) {
      setError('Failed to download pay stub');
    }
  };

  // Generate year options (current year back 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">My Pay</Typography>
        <TextField
          select size="small" label="Year" value={year}
          onChange={(e) => setYear(e.target.value)}
          sx={{ minWidth: 100 }}>
          {yearOptions.map(y => (
            <MenuItem key={y} value={y}>{y}</MenuItem>
          ))}
        </TextField>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* YTD Summary Cards */}
      {summary && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {year} Year-to-Date Summary
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <SummaryCard label="Gross Pay" value={fmt(summary.ytd_gross)} />
            <SummaryCard label="Pre-Tax Deductions" value={fmt(summary.ytd_pre_tax)} />
            <SummaryCard label="Taxes" value={fmt(summary.ytd_taxes)} />
            <SummaryCard label="Post-Tax Deductions" value={fmt(summary.ytd_post_tax)} />
            <SummaryCard label="Net Pay" value={fmt(summary.ytd_net)} highlight />
            <SummaryCard label="Regular Hours" value={parseFloat(summary.ytd_regular_hours || 0).toFixed(1)} />
            <SummaryCard label="OT Hours" value={parseFloat(summary.ytd_overtime_hours || 0).toFixed(1)} />
            <SummaryCard label="Pay Periods" value={summary.pay_periods_count || 0} />
          </Box>

          {summary.employee && (
            <Paper sx={{ mt: 2, p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Current Pay Rate</Typography>
              <Typography variant="body1">
                {summary.employee.employee_type === 'salary'
                  ? 'Salary'
                  : `${fmt(summary.employee.hourly_rate)}/hr`}
                {' · '}{(summary.employee.pay_frequency || 'biweekly').replace(/^\w/, c => c.toUpperCase())}
              </Typography>
            </Paper>
          )}

          {/* PTO Balance */}
          {summary.employee && (parseFloat(summary.employee.pto_accrual_rate || 0) > 0 || parseFloat(summary.employee.pto_balance || 0) !== 0) && (
            <Paper sx={{ mt: 2, p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Time Off Balance
                <Tooltip title="Hours you can use. Can go negative after a voided payroll run; restored when the replacement run is processed.">
                  <InfoOutlined fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                </Tooltip>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {parseFloat(summary.employee.pto_balance || 0).toFixed(1)} hrs
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Available Balance</Typography>
                </Box>
                <Box>
                  <Typography variant="body1">
                    {parseFloat(summary.employee.pto_accrual_rate || 0).toFixed(2)} hrs
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Accrual per {(summary.employee.pto_accrual_frequency || 'per_pay_period').replace('per_pay_period', 'pay period')}
                  </Typography>
                </Box>
                {parseFloat(summary.employee.pto_max_balance || 0) > 0 && (
                  <Box>
                    <Typography variant="body1">
                      {parseFloat(summary.employee.pto_max_balance).toFixed(0)} hrs
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Max Balance</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}
        </Box>
      )}

      {/* Pay Stubs List */}
      <Typography variant="h6" sx={{ mb: 1 }}>Pay Stubs</Typography>
      {stubs.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Receipt sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No pay stubs available yet</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1}>
          {stubs.map(stub => (
            <Card key={stub.id} variant="outlined">
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2">
                      {stub.period_name || `${fmtDate(stub.pay_period_start)} — ${fmtDate(stub.pay_period_end)}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {fmtDate(stub.pay_period_start)} — {fmtDate(stub.pay_period_end)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                      <Typography variant="body2">Gross: {fmt(stub.gross_pay)}</Typography>
                      <Typography variant="body2" fontWeight="bold">Net: {fmt(stub.net_pay)}</Typography>
                    </Box>
                  </Box>
                  <IconButton color="primary" onClick={() => handleDownload(stub.id)}>
                    <Download />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Pay Period</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell align="right">Gross</TableCell>
                <TableCell align="right">Net Pay</TableCell>
                <TableCell>Download</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stubs.map(stub => (
                <TableRow key={stub.id} hover>
                  <TableCell>{stub.period_name || '—'}</TableCell>
                  <TableCell>
                    {fmtDate(stub.pay_period_start)} — {fmtDate(stub.pay_period_end)}
                  </TableCell>
                  <TableCell align="right">{fmt(stub.gross_pay)}</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">{fmt(stub.net_pay)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Download PDF">
                      <IconButton size="small" color="primary" onClick={() => handleDownload(stub.id)}>
                        <Download />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ mt: 3 }}>
        <W2Documents />
      </Box>
    </Box>
  );
}

function SummaryCard({ label, value, highlight }) {
  return (
    <Box sx={{
      bgcolor: highlight ? 'primary.main' : 'action.hover',
      color: highlight ? 'primary.contrastText' : 'text.primary',
      borderRadius: 1, px: 2, py: 1, minWidth: 100,
    }}>
      <Typography variant="caption" sx={{ opacity: 0.8 }}>{label}</Typography>
      <Typography variant="h6" fontWeight="bold">{value}</Typography>
    </Box>
  );
}
